import React, { useState, useEffect } from 'react';
import '../CRMProfileScreen.css';
import axiosInstance from '../../utils/axiosConfig';
import { getErrorMessage } from '../../utils/errorMessages';

const CRMExternalDataTab = ({ profileData, onDataChange, isNewProfile }) => {
    // State management
    const [userGuidance, setUserGuidance] = useState('');
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisHistory, setAnalysisHistory] = useState([]);
    // Remove analysisProgress state - no longer needed
    const [displayedAnalysisCount, setDisplayedAnalysisCount] = useState(5);
    const [deletingAnalysis, setDeletingAnalysis] = useState(null);

    // Flash message integration
    const showSuccess = (message) => {
        // This should integrate with your existing flash message system
        console.log('SUCCESS:', message);
    };

    const showError = (message) => {
        // This should integrate with your existing flash message system
        console.error('ERROR:', message);
    };

    // Fetch analysis history when component mounts or profile changes
    useEffect(() => {
        if (profileData?.id && !isNewProfile) {
            fetchAnalysisHistory();
        }
    }, [profileData?.id, isNewProfile]);

    const fetchAnalysisHistory = async () => {
        if (!profileData?.id) {
            console.log('⚠️ FRONTEND: Cannot fetch analysis history - no profile ID');
            return;
        }
        
        try {
            console.log('🔄 FRONTEND: Fetching external data analysis history for partner:', profileData.id);
            
            const response = await axiosInstance.get(`/api/crm/partners/${profileData.id}/external-data/history/`);
            
            console.log('📊 FRONTEND: Analysis history response:', response.data);
            
            const newHistory = response.data.analysis_history || [];
            console.log('📊 FRONTEND: Setting new analysis history, length:', newHistory.length);
            setAnalysisHistory(newHistory);
            
            // Reset displayed count when fetching fresh data
            setDisplayedAnalysisCount(5);
            
            console.log('✅ FRONTEND: Analysis history fetch completed successfully');
        } catch (error) {
            console.error('❌ FRONTEND: Error fetching analysis history:', error);
            setAnalysisHistory([]);
        }
    };

    const runExternalDataAnalysis = async () => {
        if (!profileData?.id) {
            showError('Please save the partner profile first before running analysis');
            return;
        }

        console.log('🚀 FRONTEND: Starting External Data Analysis');
        console.log(`🚀 Partner ID: ${profileData?.id}`);
        console.log(`🚀 User guidance: ${userGuidance}`);

        setAnalyzing(true);
        // setAnalysisProgress(0); // Removed as per edit hint

        try {
            // Simulate progress updates
            // const progressInterval = setInterval(() => { // Removed as per edit hint
            //     setAnalysisProgress(prev => Math.min(prev + 10, 90)); // Removed as per edit hint
            // }, 1000); // Removed as per edit hint

            const requestPayload = {
                user_guidance: userGuidance
            };
            console.log('📤 Full request payload:', requestPayload);

            console.log('🌐 FRONTEND: Sending POST request to backend...');
            const response = await axiosInstance.post(
                `/api/crm/partners/${profileData.id}/external-data/analyze/`,
                requestPayload
            );

            // clearInterval(progressInterval); // Removed as per edit hint
            // setAnalysisProgress(100); // Removed as per edit hint

            console.log('📥 FRONTEND: Response received:', response.status, response.data);

            if (response.data.success) {
                console.log('✅ FRONTEND: External Data Analysis completed successfully');
                console.log('✅ Analysis ID:', response.data.analysis_id);
                console.log('✅ Processing metadata:', response.data.processing_metadata);
                
                // Add a small delay before refreshing to ensure database write is complete
                setTimeout(async () => {
                    console.log('🔄 FRONTEND: Refreshing analysis history after successful analysis...');
                    await fetchAnalysisHistory();
                }, 1500);
                
                // Clear inputs
                setUserGuidance('');
                
                showSuccess('External data analysis completed successfully!');
            } else {
                console.error('❌ FRONTEND: Analysis failed - success flag false:', response.data);
                showError('Analysis completed but returned failure status');
            }

            // Handle flash messages from backend
            if (response.data.flash_message) {
                if (response.data.flash_message.type === 'warning') {
                    console.warn('⚠️ FLASH WARNING:', response.data.flash_message.message);
                }
            }
        } catch (error) {
            console.error('❌ FRONTEND: Error running external data analysis:', error);
            console.error('❌ Error details:', error.response?.data);
            
            showError(getErrorMessage(error, 'External data analysis failed. Please try again.'));
            
            // Handle flash messages from error response
            if (error.response?.data?.flash_message) {
                console.warn('⚠️ FLASH WARNING:', error.response.data.flash_message.message);
            }
        } finally {
            setAnalyzing(false);
            // setAnalysisProgress(0); // Removed as per edit hint
        }
    };

    // Delete individual analysis function
    const deleteExternalDataAnalysis = async (analysisId, analysisTimestamp) => {
        if (!analysisId || !profileData?.id) {
            console.error('🗑️ FRONTEND: Invalid analysis or profile data');
            showError('Invalid analysis or profile data');
            return;
        }

        console.log(`🗑️ FRONTEND: Starting delete analysis ${analysisId} for partner ${profileData.id}`);
        console.log(`🗑️ FRONTEND: Analysis timestamp: ${analysisTimestamp}`);

        setDeletingAnalysis(analysisId);
        
        try {
            console.log(`🗑️ FRONTEND: Sending DELETE request...`);
            
            const response = await axiosInstance.delete(
                `/api/crm/partners/${profileData.id}/external-data/analysis/${analysisId}/delete/`
            );

            console.log('🗑️ FRONTEND: Delete response:', response.data);

            if (response.data.success) {
                console.log('✅ FRONTEND: Analysis delete request successful');
                
                // Remove the analysis from local state immediately for better UX
                const filteredHistory = analysisHistory.filter(analysis => analysis.analysis_id !== analysisId);
                setAnalysisHistory(filteredHistory);
                
                // Also refresh from server to ensure consistency
                setTimeout(() => {
                    console.log('🗑️ FRONTEND: Refreshing from server for consistency check...');
                    fetchAnalysisHistory();
                }, 1000);
                
                showSuccess(`Analysis from ${new Date(analysisTimestamp).toLocaleDateString()} deleted successfully`);
            } else {
                console.error('🗑️ FRONTEND: Delete failed - server returned error:', response.data);
                showError('Failed to delete analysis - server returned error');
            }
        } catch (error) {
            console.error('❌ FRONTEND: Error deleting analysis:', error);
            showError(getErrorMessage(error, 'External data analysis failed. Please try again.'));
        } finally {
            setDeletingAnalysis(null);
        }
    };

    // Load more analyses function
    const loadMoreAnalyses = () => {
        setDisplayedAnalysisCount(prev => prev + 5);
    };

    return (
        <div className="crm-tab-panel">
            {/* Analysis Input Section */}
            <div className="crm-form-section">
                <h2 className="crm-section-title">External Data Intelligence Analysis</h2>
                
                <div className="crm-analysis-description" style={{ marginTop: '0.75rem', marginBottom: '1rem' }}>
                    <p style={{ margin: '0.5rem 0', lineHeight: '1.6' }}>
                        The AI will perform a comprehensive internet search to gather intelligence on 
                        latest partner news, ESG & sustainability initiatives, legal disputes & regulatory matters,
                        online information, and shareholder data. You can provide optional guidance to focus the analysis.
                    </p>
                </div>
                
                {/* User Guidance Input */}
                <div className="crm-input-group">
                    <label className="crm-input-label">Analysis Guidance (Optional)</label>
                    <textarea
                        className="crm-textarea"
                        rows={4}
                        value={userGuidance}
                        onChange={(e) => setUserGuidance(e.target.value)}
                        placeholder="Provide specific guidance for the analysis, such as focusing on recent regulatory changes, sustainability initiatives, or particular business areas..."
                        disabled={analyzing}
                    />
                </div>
                
                {/* Analysis Button */}
                <button
                    type="button"
                    onClick={runExternalDataAnalysis}
                    disabled={analyzing || isNewProfile || !profileData?.id}
                    className="crm-btn-primary"
                    style={{
                        marginTop: '1rem',
                        background: (analyzing || isNewProfile || !profileData?.id) 
                            ? '#6c757d' 
                            : undefined,
                        cursor: (analyzing || isNewProfile || !profileData?.id) ? 'not-allowed' : 'pointer',
                        opacity: (analyzing || isNewProfile || !profileData?.id) ? 0.6 : 1
                    }}
                >
                    {analyzing ? 'Analyzing...' : 'Run External Data Analysis'}
                </button>
                
                {/* Helper text for disabled state */}
                {(isNewProfile || !profileData?.id) && (
                    <div style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        fontStyle: 'italic',
                        marginTop: '0.5rem',
                        lineHeight: '1.5'
                    }}>
                        Save profile first to run external data analysis
                    </div>
                )}
            </div>
            
            {/* Analysis History */}
            <div className="crm-form-section">
                <h2 className="crm-section-title">Analysis Results History</h2>
                
                {analysisHistory.length === 0 ? (
                    <div className="no-analysis-message">
                        No external data analyses performed yet. Enter guidance and run analysis.
                    </div>
                ) : (
                    <div className="analysis-history">
                        {analysisHistory.slice(0, displayedAnalysisCount).map((analysis, index) => (
                            <ExternalDataAnalysisBlock
                                key={analysis.analysis_id || index}
                                analysis={analysis}
                                isLatest={index === 0}
                                onDelete={deleteExternalDataAnalysis}
                                deletingAnalysis={deletingAnalysis}
                            />
                        ))}
                        
                        {/* Load More Button */}
                        {analysisHistory.length > displayedAnalysisCount && (
                            <div style={{ 
                                textAlign: 'center', 
                                marginTop: '1rem',
                                paddingTop: '1rem',
                                borderTop: '1px solid #e5e7eb'
                            }}>
                                <button
                                    type="button"
                                    onClick={loadMoreAnalyses}
                                    style={{
                                        background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '0.75rem 1.5rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 4px rgba(107, 114, 128, 0.15)'
                                    }}
                                    onMouseOver={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #4b5563, #374151)';
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 4px 8px rgba(107, 114, 128, 0.25)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.target.style.background = 'linear-gradient(135deg, #6b7280, #4b5563)';
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 2px 4px rgba(107, 114, 128, 0.15)';
                                    }}
                                >
                                    Load More Analyses ({analysisHistory.length - displayedAnalysisCount} remaining)
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// External Data Analysis Block Component - Styled to match AI Notes
const ExternalDataAnalysisBlock = ({ analysis, isLatest, onDelete, deletingAnalysis }) => {
    const [isExpanded, setIsExpanded] = useState(isLatest);

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDelete = () => {
        if (onDelete && analysis.analysis_id) {
            onDelete(analysis.analysis_id, analysis.timestamp);
        }
    };

    const isDeleting = deletingAnalysis === analysis.analysis_id;

    return (
        <div className={`analysis-block ${isLatest ? 'latest' : ''}`} style={{
            border: isLatest ? '2px solid #3b82f6' : '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: isLatest ? '#f0f9ff' : '#ffffff'
        }}>
            <div 
                className="analysis-header"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ cursor: 'pointer' }}
            >
                <div className="analysis-title" style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem'
                }}>
                    <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', flex: 1 }}>
                        External Data Analysis - {formatTimestamp(analysis.timestamp)}
                        {isLatest && <span style={{ 
                            marginLeft: '0.5rem',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                        }}>Latest</span>}
                    </h3>
                </div>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {analysis.processing_metadata?.total_results_found || 0} results found,
                        {analysis.user_guidance ? ' user guidance provided' : ' no user guidance'}
                    </span>
                    <span style={{ 
                        fontSize: '1rem', 
                        color: '#6b7280',
                        fontWeight: '600',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                    }}>
                        ⌄
                    </span>
                </div>
            </div>

            {isExpanded && (
                <div className="analysis-content" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    {/* User Guidance Section */}
                    {analysis.user_guidance && (
                        <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>User Guidance</h4>
                            <div style={{
                                padding: '0.75rem',
                                backgroundColor: '#f9fafb',
                                borderRadius: '0.375rem',
                                border: '1px solid #e5e7eb',
                                fontSize: '0.875rem',
                                color: '#374151'
                            }}>
                                {analysis.user_guidance}
                            </div>
                        </div>
                    )}

                    {/* Analysis Results Sections */}
                    <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>External Data Intelligence Results</h4>
                        {analysis.analysis_results ? (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                {[
                                    { key: 'partner_news', label: 'Partner News', bg: '#f0f9ff', border: '#3b82f6', color: '#1e40af' },
                                    { key: 'esg_news', label: 'ESG News', bg: '#f0fdf4', border: '#22c55e', color: '#15803d' },
                                    { key: 'legal_disputes', label: 'Legal Disputes', bg: '#fef2f2', border: '#ef4444', color: '#dc2626' },
                                    { key: 'online_information', label: 'Online Information', bg: '#fafafa', border: '#6b7280', color: '#374151' },
                                    { key: 'shareholder_information', label: 'Shareholder Information', bg: '#f3e8ff', border: '#8b5cf6', color: '#7c3aed' }
                                ].map(({ key, label, bg, border, color }) => {
                                    const content = analysis.analysis_results[key];
                                    if (!content || !content.trim()) return null;
                                    const sources = analysis.source_urls?.[key] || [];
                                    return (
                                        <div key={key} style={{ padding: '1rem', backgroundColor: bg, borderRadius: '0.5rem', border: `1px solid ${border}` }}>
                                            <h5 style={{ margin: '0 0 0.5rem 0', color, fontWeight: '600' }}>{label}</h5>
                                            <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: '1.5' }}>{content}</p>
                                            {sources.length > 0 && (
                                                <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: `1px solid ${border}40` }}>
                                                    <span style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Sources</span>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginTop: '0.25rem' }}>
                                                        {sources.slice(0, 8).map((src, i) => (
                                                            <a
                                                                key={i}
                                                                href={src.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={src.url}
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.25rem',
                                                                    padding: '0.2rem 0.5rem',
                                                                    fontSize: '0.7rem',
                                                                    color: '#2563eb',
                                                                    backgroundColor: '#ffffff',
                                                                    border: '1px solid #dbeafe',
                                                                    borderRadius: '999px',
                                                                    textDecoration: 'none',
                                                                    maxWidth: '220px',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    transition: 'all 0.15s'
                                                                }}
                                                                onMouseEnter={e => { e.target.style.backgroundColor = '#eff6ff'; e.target.style.borderColor = '#93c5fd'; }}
                                                                onMouseLeave={e => { e.target.style.backgroundColor = '#ffffff'; e.target.style.borderColor = '#dbeafe'; }}
                                                            >
                                                                <i className="fas fa-external-link-alt" style={{ fontSize: '0.6rem' }}></i>
                                                                {src.title || (() => { try { return new URL(src.url).hostname; } catch { return src.url; } })()}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: '#fef2f2',
                                borderRadius: '0.375rem',
                                border: '1px solid #ef4444',
                                fontSize: '0.875rem',
                                color: '#dc2626'
                            }}>
                                No analysis results available. There may have been an error during processing.
                            </div>
                        )}
                    </div>

                    {/* Processing Metadata */}
                    {analysis.processing_metadata && (
                        <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>Processing Information</h4>
                            <div style={{
                                padding: '0.75rem',
                                backgroundColor: '#f8fafc',
                                borderRadius: '0.375rem',
                                border: '1px solid #94a3b8',
                                fontSize: '0.875rem',
                                color: '#475569'
                            }}>
                                <p style={{ margin: '0 0 0.5rem 0' }}>
                                    <strong>Sources Searched:</strong> {analysis.processing_metadata.sources_searched?.join(', ') || 'N/A'}
                                </p>
                                <p style={{ margin: '0 0 0.5rem 0' }}>
                                    <strong>Analysis Status:</strong> {analysis.processing_metadata.analysis_successful ? 'Successful' : 'Partial/Failed'}
                                </p>
                                {analysis.processing_metadata.api_errors?.length > 0 && (
                                    <p style={{ margin: 0 }}>
                                        <strong>API Issues:</strong> Some data sources were temporarily unavailable
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Delete Button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent expanding/collapsing when clicking delete
                            handleDelete();
                        }}
                        disabled={isDeleting}
                        style={{
                            background: isDeleting ? '#6b7280' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.4rem 0.8rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: isDeleting ? 'not-allowed' : 'pointer',
                            minWidth: '80px',
                            transition: 'all 0.2s',
                            boxShadow: isDeleting ? 'none' : '0 2px 4px rgba(220, 38, 38, 0.15)',
                            opacity: isDeleting ? 0.6 : 1,
                            marginTop: '1rem'
                        }}
                        title="Delete this analysis"
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CRMExternalDataTab; 